import { CostPriceAnalysis } from '../../../services/costPrice/costPriceService';
import { Subrecipe } from '../../../services/subrecipes/subrecipesService';
import { inventoryKey } from '../../../utils/reports/consumptionReport';
import { PerDishUsage } from '../../../utils/reports/inventoryVariance';
import { PerUnitNeedsIndex } from '../../../utils/reports/utilizationReport';

/** One cost price this product is configured in. */
export interface LeakageSetup {
  costPriceId: string;
  name: string;
  /** Configured ONLY as an ingredient alternative — never as the primary choice. */
  viaAlternative: boolean;
  /** Reached through this subrecipe rather than listed on the dish directly. */
  viaSubrecipe?: string;
}

/** How a product appears in one ingredient list: not at all, as an alternative, or directly. */
type Hit = 'none' | 'alternative' | 'primary';

const stronger = (a: Hit, b: Hit): Hit => (a === 'primary' || b === 'primary' ? 'primary' : a === 'alternative' || b === 'alternative' ? 'alternative' : 'none');

/** Ingredient rows as both cost prices and subrecipes carry them. */
interface IngredientLike {
  platform?: string;
  articleId?: string;
  productName?: string;
  subrecipeId?: string;
  alternatives?: { platform: string; articleId: string; productName: string }[];
}

/**
 * How one ingredient list touches EVERY product key it mentions, using the SAME identity rule
 * as `buildCostPriceInventoryKeys` (inventoryReviewShared): an ingredient row matches through
 * `inventoryKey(platform, articleId, productName)`, and so does every one of its alternatives.
 * Rows without a platform are subrecipe / nested references and carry no product identity.
 *
 * Keyed rather than "does it match this one product" so a whole restaurant's setups can be
 * indexed in ONE pass — the export needs them for every row on screen at once.
 */
function scanIngredientsByKey(ingredients: IngredientLike[]): Map<string, Hit> {
  const hits = new Map<string, Hit>();
  const mark = (key: string, hit: Hit) => {
    if (key) hits.set(key, stronger(hits.get(key) ?? 'none', hit));
  };
  for (const ing of ingredients) {
    if (ing.platform) mark(inventoryKey(ing.platform, ing.articleId, ing.productName), 'primary');
    for (const alt of ing.alternatives ?? []) {
      mark(inventoryKey(alt.platform, alt.articleId, alt.productName), 'alternative');
    }
  }
  return hits;
}

/**
 * Every cost price a product is set up in — the answer to "where am I configured", which is a
 * different question from the drawer's per-dish usage ("what was actually used this period").
 * A dish that sold nothing, or that lists the product only as a fallback, is invisible in usage
 * yet is exactly what someone checking a link needs to see.
 *
 * Reuses the resolver-key matching of `buildCostPriceInventoryKeys` rather than re-deriving
 * product identity (see dataRules: identity goes through `resolveProductIdentifier`), but keeps
 * per-analysis attribution instead of flattening every hit into one set.
 *
 * Subrecipe-mediated setups are included and labelled: the product sits in the subrecipe, and
 * the dish is where a user would go to change how much of it is used. Nested cost-price
 * references are not followed — the nested analysis appears in this list on its own.
 */
export function buildProductSetups(
  key: string,
  costPriceList: CostPriceAnalysis[],
  subrecipesById: Map<string, Subrecipe>
): LeakageSetup[] {
  if (!key) return [];
  return buildProductSetupsIndex(costPriceList, subrecipesById).get(key) ?? [];
}

/**
 * The same answer for EVERY product at once: product key → the cost prices it is set up in.
 *
 * One pass over the recipes instead of one pass per product, which is what the detailed
 * leakage export needs — it asks for hundreds of rows in a single click, and re-scanning the
 * whole cost-price list per row is quadratic on exactly the restaurants with the most recipes.
 * `buildProductSetups` is a lookup into this, so both paths share one scan implementation.
 */
export function buildProductSetupsIndex(
  costPriceList: CostPriceAnalysis[],
  subrecipesById: Map<string, Subrecipe>
): Map<string, LeakageSetup[]> {
  // Which products each subrecipe holds, and how.
  const subHitsById = new Map<string, Map<string, Hit>>();
  subrecipesById.forEach((sub, id) => {
    subHitsById.set(id, scanIngredientsByKey((sub.ingredients ?? []) as IngredientLike[]));
  });

  const index = new Map<string, LeakageSetup[]>();
  for (const cpa of costPriceList) {
    const ingredients = (cpa.ingredients ?? []) as IngredientLike[];
    const direct = scanIngredientsByKey(ingredients);
    const hits = new Map(direct);
    const viaSubrecipeByKey = new Map<string, string | undefined>();

    for (const ing of ingredients) {
      const subHits = ing.subrecipeId ? subHitsById.get(ing.subrecipeId) : undefined;
      if (!subHits) continue;
      const subName = subrecipesById.get(ing.subrecipeId as string)?.name;
      subHits.forEach((subHit, key) => {
        // A product the dish lists DIRECTLY as a primary is chosen on the dish, not in the
        // subrecipe — that setup stays unlabelled, as it was before this index existed.
        if (direct.get(key) === 'primary') return;
        hits.set(key, stronger(hits.get(key) ?? 'none', subHit));
        viaSubrecipeByKey.set(key, subName ?? viaSubrecipeByKey.get(key));
      });
    }

    hits.forEach((hit, key) => {
      if (hit === 'none') return;
      const list = index.get(key) ?? [];
      list.push({
        costPriceId: cpa.id,
        name: cpa.costPriceName || cpa.menuItemName || '',
        viaAlternative: hit === 'alternative',
        viaSubrecipe: viaSubrecipeByKey.get(key),
      });
      index.set(key, list);
    });
  }

  // Direct setups first — they are where the product is actually chosen — then by name.
  index.forEach((setups) =>
    setups.sort((a, b) => {
      if (a.viaAlternative !== b.viaAlternative) return a.viaAlternative ? 1 : -1;
      return a.name.localeCompare(b.name);
    })
  );
  return index;
}

/** A breakdown line: what a recipe used this period, plus where the product is configured. */
export type UsageWithSetup = PerDishUsage & {
  costPriceId?: string;
  /** Configured only as an ingredient alternative — badged in the drawer. */
  viaAlternative?: boolean;
  /** Reached through this subrecipe rather than listed on the dish directly. */
  viaSubrecipe?: string;
};

/**
 * Fold a product's setups into its per-dish usage so ONE section answers both "what was used"
 * and "where am I configured" — the product-utilization drawer's shape.
 *
 * Two kinds of setup produce no usage line at all and would otherwise be invisible:
 *   - a recipe that sold nothing in the window, and
 *   - a recipe listing the product only as a FALLBACK, because `buildPerDishUsage` walks
 *     primaries only (leakage has no purchase context to allocate against).
 * Both are appended as zero-sold cards, carrying the per-portion amount from the recipe so the
 * card reads "120 g × 0 sold = 0" instead of an empty formula.
 *
 * Dedupe is by `costPriceId`, never by label: a usage label is a menu-item (revenue) name while
 * a setup is named after its analysis, and those legitimately differ — matching on text would
 * list the same dish twice.
 */
export function mergeSetupsIntoUsage(
  sold: UsageWithSetup[],
  args: {
    productKey: string;
    costPriceList: CostPriceAnalysis[];
    subrecipesById: Map<string, Subrecipe>;
    perUnit: PerUnitNeedsIndex;
    /**
     * `row.neededUnitFactor` — base units per one unit of the ROW's space, when the leakage
     * engine had to convert the recipes' unit space into the counts' (a 3 L bottle counted
     * per piece → 3000). The sold lines arrive already converted (`buildPerDishUsage` scales
     * them onto the row total); these cards are read straight off the recipes, so they must
     * be divided here or the drawer prints "3000 pcs per dish" beside a 1-bottle row.
     */
    basePerUnitDivisor?: number;
  }
): UsageWithSetup[] {
  const { productKey, costPriceList, subrecipesById, perUnit, basePerUnitDivisor } = args;
  const seen = new Set(sold.map((u) => u.costPriceId).filter(Boolean));
  const menuItemById = new Map(costPriceList.map((a) => [a.id, a.menuItemName]));
  const needsByDish = new Map(perUnit.dishes.map((d) => [d.menuItemName, d.needs]));

  const unsold = buildProductSetups(productKey, costPriceList, subrecipesById)
    .filter((setup) => !seen.has(setup.costPriceId))
    .map((setup): UsageWithSetup => {
      const menuItemName = menuItemById.get(setup.costPriceId);
      const needs = menuItemName ? needsByDish.get(menuItemName) ?? [] : [];
      // Every option is scanned, not just the primary — that is the whole point for a setup
      // where this product is the alternative.
      let basePerUnit = 0;
      for (const need of needs) {
        const option = need.options.find((o) => o.key === productKey);
        if (option) basePerUnit += need.portions * option.base;
      }
      return {
        label: setup.name,
        unitsSold: 0,
        basePerUnit: basePerUnitDivisor ? basePerUnit / basePerUnitDivisor : basePerUnit,
        totalBase: 0,
        costPriceId: setup.costPriceId,
        viaAlternative: setup.viaAlternative,
        viaSubrecipe: setup.viaSubrecipe,
      };
    });

  return [...sold, ...unsold];
}
